import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useProducts, usePage } from "@/lib/hooks";
import ProductCard from "@/components/ProductCard";
import SectionRenderer from "@/components/sections/SectionRenderer";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

const FILTERS = [
  { label: "All", value: "all" }, { label: "Digital", value: "DIGITAL" },
  { label: "Physical", value: "PHYSICAL" }, { label: "Hybrid", value: "HYBRID" },
  { label: "Bundles", value: "BUNDLE" },
];
const SORTS = [
  { label: "Featured", value: "featured" }, { label: "Newest", value: "newest" },
  { label: "Price: Low to High", value: "price_asc" }, { label: "Price: High to Low", value: "price_desc" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [type, setType] = useState(params.get("type") || "all");
  const [sort, setSort] = useState("featured");
  const [q, setQ] = useState("");
  const searchRef = useRef(null);
  const { data: products, isLoading } = useProducts({ type, sort, ...(q ? { q } : {}) });
  const { data: page } = usePage("shop");

  useEffect(() => { if (params.get("focus")) searchRef.current?.focus(); }, [params]);

  return (
    <div>
      <SectionRenderer sections={page?.sections || []} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="mb-6">
        <span className="eyebrow">Shop</span>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-navy">All Products</h1>
        <p className="mt-2 text-slate-500">Business systems, tools and planners for your RO / water purifier service business.</p>
      </div>

      <div className="relative mb-5 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input ref={searchRef} placeholder="Search products…" value={q} onChange={(e) => setQ(e.target.value)}
          className="pl-9 bg-white" data-testid="shop-search-input" />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-8">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {FILTERS.map((f) => (
            <button key={f.value} onClick={() => setType(f.value)} data-testid={`filter-${f.value.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap border transition-colors ${type === f.value ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-teal-500/50"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="shop-sort"
          className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-teal outline-none">
          {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => <div key={i} className="h-80 bg-white border border-slate-200 rounded-xl animate-pulse" />)}
        </div>
      ) : products?.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shop-grid">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      ) : (
        <p className="text-center text-slate-500 py-16">No products found.</p>
      )}
      </div>
    </div>
  );
}
